import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Divider } from '@mantine/core';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { singularPluralFormat } from '../../../helpers/format';
import ResponsiveModal from '../../common/ResponsiveModal';

const ExternalProductImportModal = ({
  externalProducts,
  isOpen,
  onClose,
  config,
  onSubmit,
  isLoading,
  formState,
  setFormState
}) => {
  const canCustomize = config?.products?.restrictions?.canCustomizeDesign;

  return (
    <ResponsiveModal
      formSectionProps={
        canCustomize
          ? null
          : {
              onCancel: onClose,
              onSubmit,
              submitColor: 'dark',
              submitTitle: 'Yes, import products',
              isSubmitDisabled: false,
              cancelTitle: 'Cancel',
              isLoading
            }
      }
      onClose={onClose}
      opened={isOpen}
      size={canCustomize ? 500 : 600}
      title={`Import ${singularPluralFormat(
        externalProducts?.length || 0,
        'product',
        'products'
      )} from ${config?.title}`}
    >
      <AppStack style={{ gap: 25, paddingBottom: 10 }}>
        {canCustomize ? (
          <>
            <AppText style={{ fontSize: 14 }}>
              These products give you the option to customize their design
              before you bring them into your store. To make changes, click
              ‘Customize Design,’ adjust the product as you like, and then
              continue with the import.
            </AppText>
            <Button
              color="blue"
              component={Link}
              size="md"
              to={`/vendor/products/import/${
                config.routeValue
              }/design?ids=${externalProducts
                .map((p) => p.productExternalId)
                .join(',')}`}
            >
              Customize Design
            </Button>
          </>
        ) : (
          <AppStack style={{ gap: 16 }}>
            <AppText style={{ fontSize: 16 }}>
              Are you sure you want to import{' '}
              <span style={{ fontWeight: 500 }}>
                {singularPluralFormat(
                  externalProducts.length,
                  'product',
                  'products'
                )}
              </span>{' '}
              from {config?.title}?
            </AppText>

            <Checkbox
              checked={formState.setProductToLive || false}
              description="This will make them visible in the catalog right away. Otherwise they will be hidden until you change their status."
              disabled={isLoading}
              label="Make products live after adding."
              onChange={() => {
                setFormState({
                  ...formState,
                  setProductToLive: !formState.setProductToLive
                });
              }}
              styles={{ label: { fontWeight: 500 } }}
            />

            <Checkbox
              checked={formState.isPrivate || false}
              description="When selected, this product will be hidden from the public catalog but can still be added to any store"
              disabled={isLoading}
              label="Visible to SportsHeadz admins only"
              onChange={() => {
                setFormState({
                  ...formState,
                  isPrivate: !formState.isPrivate
                });
              }}
              styles={{ label: { fontWeight: 500 } }}
            />

            <Divider />

            <Checkbox
              checked={formState.switchToBulkEditor}
              disabled={isLoading}
              label="Switch to bulk editor after importing"
              onChange={() => {
                setFormState({
                  ...formState,
                  switchToBulkEditor: !formState.switchToBulkEditor
                });
              }}
              styles={{ label: { fontWeight: 500 } }}
            />
          </AppStack>
        )}
      </AppStack>
    </ResponsiveModal>
  );
};

ExternalProductImportModal.propTypes = {
  config: PropTypes.object.isRequired,
  externalProducts: PropTypes.array.isRequired,
  formState: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setFormState: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

export default ExternalProductImportModal;
